import * as React from 'react'
import clsx from 'clsx'
import { useForkRef } from '@applift/factor'
import { useGridApiContext } from '../hooks/utils/useGridApiContext'
import { useGridRootProps } from '../hooks/utils/useGridRootProps'
import { DataGridProps } from '../models/props/DataGridProps'
import { gridColumnsTotalWidthSelector } from '../hooks/features/columns/gridColumnsSelector'
import { GridCellProps } from './cell/GridCell'
import {
  GridReactTableInstance,
  ReactTableColumn,
} from '../models/gridReactTable'
import { ReactVirtualItem } from '../models/gridReactVirtual'
import styles from '../style'

export interface GridSkeletonRowProps extends GridReactTableInstance {
  rowHeight: number | 'auto'
  containerWidth: number
  side: 'left' | 'center' | 'right'
  colRange: ReactVirtualItem[]
  index: number
}

function EmptyCell({
  width,
  classes,
}: {
  width: number
  classes: DataGridProps['classes']
}) {
  if (!width) {
    return null
  }
  return <div className={clsx(styles.cell, classes?.cell)} style={{ width }} />
}

export const GridSkeletonRow = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & GridSkeletonRowProps
>(function GridRow(props, refProp) {
  const {
    index: rowId,
    index,
    style: styleProp,
    rowHeight,
    containerWidth,
    getLeftVisibleLeafColumns,
    getRightVisibleLeafColumns,
    getCenterVisibleLeafColumns,
    getVisibleLeafColumns,
    side,
    colRange,
  } = props
  const apiRef = useGridApiContext()
  const ref = React.useRef<HTMLDivElement>(null)
  const rootProps = useGridRootProps()
  const columnsTotalWidth = gridColumnsTotalWidthSelector(apiRef.current)
  const headersMaxDepth = apiRef.current.tableInstance.getHeaderGroups().length
  const showColumnGrouping = rootProps.showColumnGrouping
  const handleRef = useForkRef(ref, refProp)
  const ariaRowIndex = index + (showColumnGrouping ? headersMaxDepth + 1 : 2)
  const { hasScrollX } = apiRef.current.getRootDimensions() ?? {
    hasScrollX: false,
    hasScrollY: false,
  }

  const emptyCellWidth =
    side === 'center' ? containerWidth - columnsTotalWidth : 0
  const minHeight = rowHeight
  const style = {
    ...styleProp,
    maxHeight: rowHeight === 'auto' ? 'none' : rowHeight, // max-height doesn't support "auto"
    minHeight,
  }
  const cellsToRender =
    side === 'left'
      ? getLeftVisibleLeafColumns()
      : side === 'right'
        ? getRightVisibleLeafColumns()
        : getCenterVisibleLeafColumns()

  const firstColumnToRender =
    side === 'left'
      ? 0
      : side === 'right'
        ? getLeftVisibleLeafColumns().length +
          getCenterVisibleLeafColumns().length
        : getLeftVisibleLeafColumns().length

  const visibleColumns = getVisibleLeafColumns().length - 1
  const rightCellsLength = getRightVisibleLeafColumns().length

  const getCell = React.useCallback(
    (
      column: ReactTableColumn,
      cellProps: Pick<GridCellProps, 'showRightBorder' | 'colIndex'>
    ) => {
      return (
        <rootProps.components.SkeletonCell
          {...rootProps.componentsProps?.cell}
          key={column.id}
          height={rowHeight}
          rowId={rowId}
          showRightBorder={cellProps.showRightBorder}
          colIndex={cellProps.colIndex}
          contentWidth={80}
          {...column}
        />
      )
    },
    [rootProps, rowHeight, rowId]
  )

  const cells: JSX.Element[] = []

  if (side === 'left' || side === 'right') {
    for (let i = 0; i < cellsToRender.length; i++) {
      cells.push(
        getCell(cellsToRender[i]!, {
          showRightBorder: rootProps.showCellRightBorder,
          colIndex: firstColumnToRender + i,
        })
      )
    }
  } else {
    for (let i = 0; i < colRange.length; i += 1) {
      const currentIndex = colRange[i]!.index!
      const currentCell = cellsToRender[currentIndex]!
      const indexRelativeToAllColumns = firstColumnToRender + currentIndex
      const isLastColumn =
        indexRelativeToAllColumns + rightCellsLength === visibleColumns
      const removeLastBorderRight = isLastColumn && !hasScrollX
      const showRightBorder = !isLastColumn
        ? rootProps.showCellRightBorder
        : !removeLastBorderRight && rootProps.disableExtendRowFullWidth
      const cellProps = {
        showRightBorder,
        colIndex: indexRelativeToAllColumns,
      }
      cells.push(getCell(currentCell, cellProps))
    }
  }

  return (
    <div
      ref={handleRef}
      data-id={rowId}
      data-rowindex={`skeleton-${index}`}
      role="row"
      className={clsx(
        styles.skeletonRow,
        rootProps.classes?.skeletonRow,
        rowHeight === 'auto' && [
          styles['row--dynamicHeight'],
          rootProps.classes?.['row--dynamicHeight'],
        ]
      )}
      aria-rowindex={ariaRowIndex}
      aria-selected={false}
      style={style}
    >
      {cells}
      {emptyCellWidth > 0 && (
        <EmptyCell width={emptyCellWidth} classes={rootProps.classes} />
      )}
    </div>
  )
})
